import { authenticatedFetch } from './authApi';
import { getCachedOrFetch } from './requestCache';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const SUPPORT_FORUM_TTL_MS = 15_000;

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const error = (payload as UnknownRecord).error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const supportForumRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, options);

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Support forum API request failed (${response.status})`));
  }

  return payload;
};

export const getLatestForumPostsApi = async (limit = 6): Promise<unknown[]> => {
  return await getCachedOrFetch(`supportForum:latest:${limit}`, SUPPORT_FORUM_TTL_MS, async () => {
    const payload = await supportForumRequest(`/api/v1/support/forum/posts/latest?limit=${encodeURIComponent(String(limit))}`, {
      method: 'GET',
    });

    if (payload && typeof payload === 'object' && 'posts' in (payload as UnknownRecord)) {
      const posts = (payload as UnknownRecord).posts;
      return Array.isArray(posts) ? posts : [];
    }
    return Array.isArray(payload) ? payload : [];
  });
};

export const getMyForumPostsApi = async (): Promise<unknown[]> => {
  return await getCachedOrFetch('supportForum:me:posts', SUPPORT_FORUM_TTL_MS, async () => {
    const payload = await supportForumRequest('/api/v1/support/forum/me/posts', {
      method: 'GET',
    });

    if (payload && typeof payload === 'object' && 'posts' in (payload as UnknownRecord)) {
      const posts = (payload as UnknownRecord).posts;
      return Array.isArray(posts) ? posts : [];
    }
    return Array.isArray(payload) ? payload : [];
  });
};
